import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  Backdrop,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";

// ─── Constants ────────────────────────────────────────────────────────────────

const LOGO_SIZE = 130; // px — rendered height of the logo
const LOGO_OVERLAP = LOGO_SIZE / 2; // px the logo rises above the dialog top edge

// ─── Types ────────────────────────────────────────────────────────────────────

interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const features: FeatureItem[] = [
  {
    icon: <LocalOfferOutlinedIcon sx={{ fontSize: 28, color: "#1A2B5F" }} />,
    title: "Preferred Partner Pricing",
    description: "Access exclusive pricing on setup and monthly services.",
  },
  {
    icon: <HandshakeOutlinedIcon sx={{ fontSize: 28, color: "#1A2B5F" }} />,
    title: "White-Glove Onboarding",
    description: "Full implementation and support handled by our team.",
  },
  {
    icon: <PersonOutlineOutlinedIcon sx={{ fontSize: 28, color: "#1A2B5F" }} />,
    title: "Dedicated Account Support",
    description: "A direct point of contact for ongoing success.",
  },
  {
    icon: <BoltOutlinedIcon sx={{ fontSize: 28, color: "#1A2B5F" }} />,
    title: "Priority Access",
    description: "Be first in line for new features and enhancements.",
  },
];

// ─── FeatureRow ───────────────────────────────────────────────────────────────

const FeatureRow: React.FC<{ item: FeatureItem }> = ({ item }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 1.5 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: "12px",
          background: "rgba(26,43,95,0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.icon}
      </Box>
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, color: "#1A1A1A", lineHeight: 1.3 }}
        >
          {item.title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
          {item.description}
        </Typography>
      </Box>
    </Box>
    <Divider sx={{ borderColor: "rgba(0,0,0,0.06)" }} />
  </Box>
);

// ─── NataPopupModal ───────────────────────────────────────────────────────────

interface NataPopupModalProps {
  open: boolean;
  onClose: () => void;
  onGetStartedClicked: () => void;
}

const NataPopupModal: React.FC<NataPopupModalProps> = ({
  open,
  onClose,
  onGetStartedClicked,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(10,10,20,0.6)",
            backdropFilter: "blur(6px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "24px",

          // ── KEY: allow the logo Box to visually escape the Paper boundary ──
          overflow: "visible",

          background: "#FAFAFA",
          boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
          mx: { xs: 1, sm: 3 },
          width: { xs: "calc(100% - 16px)", sm: "100%" },
          maxHeight: { xs: "95vh", sm: "90vh" },

          // Push the card down so the logo has vertical room above it
          mt: `${LOGO_OVERLAP}px`,

          animation: "slideUp 0.35s cubic-bezier(0.34,1.56,0.64,1)",
          "@keyframes slideUp": {
            from: { opacity: 0, transform: "translateY(40px) scale(0.96)" },
            to: { opacity: 1, transform: "translateY(0) scale(1)" },
          },
        },
      }}
    >
      {/* ── Anchored logo ─────────────────────────────────────────────────────
          • position="absolute" relative to the Paper
          • top: -LOGO_OVERLAP  →  logo sits half above / half below the top edge
          • left: 50% + translateX(-50%)  →  horizontally centred               */}
      <Box
        sx={{
          mt: 4,
          zIndex: 10,
          lineHeight: 0, // remove phantom inline spacing
          filter: "drop-shadow(0 8px 24px rgba(26,43,95,0.28))",
          pointerEvents: "none", // click-through so backdrop-close still works
        }}
      >
        <Box
          component="img"
          src="/static/images/nata_logo.png"
          alt="NATA – National Automotive Training Academy"
          sx={{
            height: `${LOGO_SIZE}px`,
            width: "auto",
            display: "block",
            mx: "auto",
          }}
        />
      </Box>

      {/* ── Scrollable content area ──────────────────────────────────────────
          overflow:"auto" here keeps the Paper itself overflow:visible        */}
      <DialogContent
        sx={{
          // Reserve space so the logo doesn't overlap the headline
          pt: `${16}px`,
          px: { xs: 3, sm: 4 },
          pb: { xs: 3, sm: 4 },
          overflowY: "auto",
          // Clip scrolling content inside rounded corners
          borderRadius: "inherit",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.15)",
            borderRadius: 2,
          },
        }}
      >
        {/* Headline */}
        <Typography
          variant="h4"
          align="center"
          sx={{
            fontSize: { xs: "1.4rem", sm: "1.85rem" },
            fontWeight: 800,
            color: "#0F172A",
            mb: 1,
          }}
        >
          Exclusive Offer for NATA Customers
        </Typography>

        <Typography
          variant="body1"
          align="center"
          sx={{ color: "#6B7280", mb: 3, px: { xs: 0, sm: 2 } }}
        >
          This experience was created specifically for NATA customers through
          our partnership with QRBuddy.
        </Typography>

        <Divider sx={{ mb: 1, borderColor: "rgba(0,0,0,0.07)" }} />

        {/* Feature list */}
        <Stack>
          {features.map((item) => (
            <FeatureRow key={item.title} item={item} />
          ))}
        </Stack>

        {/* Reserved tag */}
        <Typography
          variant="caption"
          align="center"
          display="block"
          sx={{
            mt: 2.5,
            mb: 2,
            color: "#9CA3AF",
            fontStyle: "italic",
            fontSize: "0.78rem",
          }}
        >
          Reserved exclusively for NATA customers
        </Typography>

        {/* CTA Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => {
            onClose();
            // onGetStartedClicked();
          }}
          sx={{
            bgcolor: "#E87722",
            color: "white",
            borderRadius: "100px",
            py: 1.8,
            fontSize: { xs: "1rem", sm: "1.05rem" },
            fontWeight: 800,
            letterSpacing: "0.3px",
            textTransform: "none",
            boxShadow: "0 6px 24px rgba(232,119,34,0.40)",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#d06518",
              boxShadow: "0 8px 32px rgba(232,119,34,0.55)",
              transform: "translateY(-1px)",
            },
            "&:active": {
              transform: "translateY(1px)",
              boxShadow: "0 4px 12px rgba(232,119,34,0.35)",
            },
          }}
        >
          View Exclusive Offer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default NataPopupModal;
